/**
 * Cross-document view transitions: make sure exactly one element per
 * view-transition-name is snapshotted, preferring the copy the user
 * actually interacted with.
 *
 * A post's image can appear more than once on a page (featured card +
 * feed card on the home page) — duplicate names cancel the whole
 * transition — and named elements far outside the viewport produce
 * distracting fly-across-the-screen animations. Names only matter at
 * the two snapshot moments (pageswap on the way out, pagereveal on the
 * way in), so this resolves them synchronously right there:
 *
 *   clicked copy > remembered copy (back-nav) > first copy in view.
 *
 * Server-rendered inline names remain the no-JS baseline.
 */

const SELECTOR = 'img.post-image-transition[data-vt-name]';
const SESSION_KEY = 'view-transition-source';

type WithViewTransition = { viewTransition?: { finished?: Promise<unknown> } };

/**
 * Breadcrumb persisted across the navigation: which copy the user
 * left from (so back-nav returns to it) and how large each snapshotted
 * element was rendered (so the arriving page can tell a shrinking
 * morph from a growing one and show the sharper snapshot).
 */
type State = {
    clicked?: { name: string, index: number },
    sizes?: Record<string, number>
};

export default function viewTransitionNames() {
    if (!('onpageswap' in window)) { return; }

    const groups = () => {
        const map = new Map<string, HTMLImageElement[]>();
        document.querySelectorAll(SELECTOR).forEach(el => {
            const name = (el as HTMLImageElement).dataset.vtName as string;
            map.set(name, [...(map.get(name) ?? []), el as HTMLImageElement]);
        });
        return map;
    };

    const inViewport = (el: Element) => {
        const rect = el.getBoundingClientRect();
        return rect.bottom > 0 && rect.right > 0 &&
            rect.top < document.documentElement.clientHeight &&
            rect.left < document.documentElement.clientWidth;
    };

    const captionOf = (el: HTMLImageElement) =>
        el.closest('figure.post-media')?.querySelector('figcaption') as HTMLElement | null;

    /**
     * prefer() returning an element pins that copy; `undefined` falls
     * back to the first copy in view; `null` suppresses the whole
     * group (no copy participates). The caption inside the same
     * figure is named together with its image so it morphs along.
     */
    const apply = (prefer?: (name: string, group: HTMLImageElement[]) => HTMLImageElement | null | undefined) => {
        groups().forEach((group, name) => {
            const preferred = prefer?.(name, group);
            const chosen = preferred === null ?
                undefined :
                preferred ?? group.filter(inViewport)[0];
            group.forEach(el => {
                el.style.setProperty('view-transition-name', el === chosen ? name : 'none');
                captionOf(el)?.style.setProperty(
                    'view-transition-name', el === chosen ? `caption-${name}` : 'none');
            });
        });
    };

    // Track which copy a navigation originates from: any link click
    // inside a container holding exactly one candidate image.
    let clicked: HTMLImageElement | null = null;
    document.addEventListener('click', event => {
        clicked = null;
        let node = (event.target as Element).closest('a[href]') as Element | null;
        while (node && node !== document.body) {
            const imgs = node.querySelectorAll(SELECTOR);
            if (imgs.length > 1) { return; }
            if (imgs.length === 1) {
                clicked = imgs[0] as HTMLImageElement;
                return;
            }
            node = node.parentElement;
        }
    }, { capture: true });

    const readState = (): State => {
        try {
            return JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '{}') as State;
        } catch {
            return {};
        }
    };

    const chosenOf = (group: HTMLImageElement[], name: string) =>
        group.find(el => el.style.getPropertyValue('view-transition-name') === name);

    const area = (el: Element) => {
        const rect = el.getBoundingClientRect();
        return rect.width * rect.height;
    };

    window.addEventListener('pageswap', event => {
        if (!(event as WithViewTransition).viewTransition) { return; }
        const state = readState();
        // Only groups that can pair with the other document morph:
        // after a click, just the clicked image's; on back/forward
        // navigation, just the ones that morphed on the way in.
        // Everything else would snapshot one-sided and animate (or,
        // per our CSS, vanish) instead of staying put on the page.
        apply((name, group) => {
            if (clicked) {
                return group.includes(clicked) ? clicked : null;
            }
            return state.sizes && !(name in state.sizes) ? null : undefined;
        });
        state.sizes = {};
        groups().forEach((group, name) => {
            const chosen = chosenOf(group, name);
            if (chosen) {
                state.sizes![name] = area(chosen);
            }
        });
        if (clicked) {
            const name = clicked.dataset.vtName as string;
            state.clicked = { name, index: groups().get(name)?.indexOf(clicked) ?? 0 };
        }
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
        clicked = null;
    });

    window.addEventListener('pagereveal', event => {
        const transition = (event as WithViewTransition).viewTransition;
        if (!transition) { return; }
        const state = readState();
        apply((groupName, group) => {
            if (state.sizes && !(groupName in state.sizes)) { return null; }
            const remembered = groupName === state.clicked?.name ?
                group[state.clicked.index] :
                undefined;
            return remembered && inViewport(remembered) ? remembered : undefined;
        });
        let shrink = false;
        groups().forEach((group, name) => {
            const chosen = chosenOf(group, name);
            const before = state.sizes?.[name];
            if (chosen && before) {
                shrink = shrink || before > area(chosen);
            }
        });
        // Growing morph (card → post hero): scroll the arriving page
        // so the hero's top sits at the viewport top and the zoom
        // lands there. Done synchronously inside pagereveal so the
        // snapshots are captured at the scrolled position.
        // Only post heroes want it: an author photo morphing near the
        // top of the author page should leave the scroll alone.
        if (!shrink && state.clicked?.name.startsWith('post-image-')) {
            const chosen = chosenOf(
                groups().get(state.clicked.name) ?? [], state.clicked.name);
            if (chosen && state.sizes?.[state.clicked.name]) {
                // Align the figure's border box, so the image's frame
                // (border on figure.post-media) meets the top edge.
                const frame = chosen.closest('figure.post-media') ?? chosen;
                window.scrollTo({
                    top: window.scrollY + frame.getBoundingClientRect().top,
                    behavior: 'instant'
                });
            }
        }
        document.documentElement.classList.toggle('vt-shrink', shrink);
        transition.finished?.finally(() =>
            document.documentElement.classList.remove('vt-shrink'));
    });
}
